import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, FloatingLabel, Alert, Spinner, Card, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchUserSecret, logout, patchUserData, sendMultiFactorCode, verifyTwoFactor } from "../../services/httpClient";
import QRCode from "react-qr-code";
import useApp from "../../utils/useApp";

const TwoFactorSetup = () => {
  const { auth, setAuth, setActivePage }: any = useApp();
  const [secretUrl, setSecretUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessfulSetup, setIsSuccessfulSetup] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [default2fa, setDefault2fa] = useState("app");
  const [useEmail, setUseEmail] = useState(false);
  const [usePhone, setUsePhone] = useState(false);
  const [useOtherMethod, setUseOtherMethod] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  let navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);

    fetchUserSecret()
      .then((res: any) => {
        const sec = res[0].data.secret;
        setSecret(sec);
        const environment = process.env.REACT_APP_ENVIRONMENT;
        const envSuffix = environment != "Prod" ? "-" + environment : "";
        setSecretUrl(
          "otpauth://totp/AndGo" + envSuffix + ":" + auth?.profile?.email + "?secret=" + sec + "&issuer=SelfLyft" + envSuffix + ""
        );
      })
      .catch((err: any) => {})
      .finally(() => {
        setIsLoading(false);
        inputRef?.current?.focus();
      });
  }, []);

  const sendCode = (type: "email" | "phone") => {
    const emailPhone = type === "email" ? auth?.profile?.email : auth?.profile?.phonenumber;
    setDefault2fa(type);

    sendMultiFactorCode(emailPhone, type, secret)
      .then(() => setSuccessMsg(`Verification code was sent to your ${type}.`))
      .catch((err: any) => {
        if (!err?.response) setErrMsg("No server response.");
        else setErrMsg("Failed to send code.");
      });
  };

  const handleEmailVerification = () => {
    setUseEmail(true);
    setUseOtherMethod(true);
    sendCode("email");
  };

  const handlePhoneVerification = () => {
    setUsePhone(true);
    setUseOtherMethod(true);
    sendCode("phone");
  };

  const handleTryAnotherMethod = () => {
    setDefault2fa("app");
    setUseOtherMethod(false);
    setUseEmail(false);
    setUsePhone(false);
    setSuccessMsg("");
    setErrMsg("");
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const verificationType = default2fa;

    verifyTwoFactor(code, secret, default2fa)
      .then((res: any) => {
        setIsSuccessfulSetup(true);
        setIsLoading(true);
        patchUserData({ default_2fa: verificationType }, auth?.profile?.id);
        setAuth({ ...auth, profile: { ...auth.profile, default_2fa: verificationType }, twofactor: true });

        setTimeout(() => {
          if (auth?.profile?.homepage) {
            setActivePage(auth.profile.homepage);
            navigate(auth.profile.homepage);
          } else {
            navigate("/");
          }

          setIsLoading(false);
        }, 3000);
      })
      .catch((err: any) => {
        var _attempts = attempts + 1;
        setAttempts(_attempts);
        if (!err?.response) {
          setErrMsg("No Server Response");
        } else if (err?.response?.status === 401) {
          setErrMsg("Invalid Code. Attempt " + _attempts + "/3");
          if (_attempts === 3) {
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } else {
          setErrMsg("Login Failed");
        }
      });
  };

  const handleLogout = () => {
    logout()
      .then((res: any) => {
        navigate("/login");
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  return (
    <div className="h-100 app-primary-alt-color align-content-center">
      {isLoading ? (
        <Row className="w-100 pb-5 d-flex align-item-center justify-content-center m-0">
          <Col xs={10} sm={8} md={6} lg={5} xl={4} xxl={4} className="pb-5 text-center">
            <Button size="sm" className="spinner-button">
              <Spinner animation="border" style={{ color: "#F6893D" }} />
            </Button>
          </Col>
        </Row>
      ) : (
        <Row className="w-100 pb-5 d-flex align-item-center justify-content-center m-0">
          <Col xs={10} sm={8} md={6} lg={5} xl={4} xxl={4} className="pb-5">
            <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
              {errMsg}
            </p>
            <p className={successMsg ? "successmsg-inverse" : "offscreen"} aria-live="assertive">
              {successMsg}
            </p>
            <Card className="shadow-sm rounded-lg d-block">
              <Image src="/AndGo_Logo_Color_Horizontal.png" fluid className="pt-5 px-5 pb-5" />
              <Form onSubmit={handleSubmit}>
                <h4 className="text-center app-header-text">Setup Two-Factor Authentication</h4>
                {!useEmail && !usePhone && (
                  <>
                    <h6 className="text-center text-secondary mb-3 mx-3">Scan QR Code in Authenticator App</h6>
                    <div className="text-center mb-3">
                      <QRCode value={secretUrl} />
                    </div>
                  </>
                )}
                <h6 className="p-1 text-center text-secondary ">Verify Code </h6>
                <FloatingLabel label="Code" className="mx-3 mb-3 app-header-text">
                  <Form.Control
                    type="text"
                    placeholder="Code"
                    id="user"
                    onChange={(e) => {
                      setCode(e.target.value);
                      setErrMsg("");
                    }}
                    value={code}
                    ref={inputRef}
                    required
                  />
                </FloatingLabel>
                <div className="d-grid w-100">
                  <Button className="button app-tertiary-color text-center mx-3 mb-3 p-3" type="submit">
                    Activate
                  </Button>
                </div>
                {!useEmail && !usePhone && (
                  <p className="d-flex justify-content-center gap-1 text-secondary">
                    Send code via
                    <Alert.Link onClick={handleEmailVerification}> email </Alert.Link>
                    or
                    <Alert.Link onClick={handlePhoneVerification}> text message </Alert.Link>
                    instead.
                  </p>
                )}
                {useOtherMethod && (
                  <Alert key="handleTryAnotherMethod" variant="light" className="p-1 text-center border-0 bg-transparent">
                    <Alert.Link onClick={handleTryAnotherMethod}>Try another method</Alert.Link>
                  </Alert>
                )}
                <Alert key="handleLogout" variant="light" className="p-1 text-center border-0 bg-transparent">
                  <Alert.Link onClick={handleLogout}>Back to login</Alert.Link>
                </Alert>
              </Form>
            </Card>
          </Col>
        </Row>
      )}
      {isSuccessfulSetup && (
        <Row className="pt-5 mt-5">
          <Col>
            <h4 className="p-3 text-center text-white ">Two-Factor Successfully Enabled!</h4>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TwoFactorSetup;
